import { SimpleCameraProperty } from './camera-property';
import {
    eosApertureValueMap,
    eosExposureCompensationMap,
    eosIsoSpeedMap,
    eosShutterSpeedMap,
    eosAutoPowerOffMap,
    eosIsoSpeedMapShorten,
    eosApertureValueMapShorten,
    eosShutterSpeedMapShorten,
} from './definitions';

export const eosApertureValueArray = Object.entries(eosApertureValueMap).map(([key, value]) => ({
    key: parseInt(key),
    value,
}));
export const eosApertureValueArrayShorten = Object.entries(eosApertureValueMapShorten).map(([key, value]) => ({
    key: parseInt(key),
    value,
}));

export const eosShutterSpeedArray = Object.entries(eosShutterSpeedMap).map(([key, value]) => ({
    key: parseInt(key),
    value,
}));
export const eosShutterSpeedArrayShorten = Object.entries(eosShutterSpeedMapShorten).map(([key, value]) => ({
    key: parseInt(key),
    value,
}));
export const eosIsoSpeedArray = Object.entries(eosIsoSpeedMap).map(([key, value]) => ({
    key: parseInt(key),
    value,
}));
export const eosIsoSpeedArrayShorten = Object.entries(eosIsoSpeedMapShorten).map(([key, value]) => ({
    key: parseInt(key),
    value,
}));

export const eosExposureCompensationArray = Object.entries(eosExposureCompensationMap).map(([key, value]) => ({
    key: parseInt(key),
    value,
}));
export const eosExposureCompensationArrayShorten = eosExposureCompensationArray;

const COLOR_TEMPERATURE_CONFIG = {
    MIN_K: 2800,
    CAP_K: 8000,
    MAX_K: 10000,
    STEP_K: 100,
};
export const colorTemperatureArray = Array.from(
    { length: (COLOR_TEMPERATURE_CONFIG.MAX_K - COLOR_TEMPERATURE_CONFIG.MIN_K) / COLOR_TEMPERATURE_CONFIG.STEP_K + 1 },
    (_, i) => ({
        key: COLOR_TEMPERATURE_CONFIG.MIN_K + i * COLOR_TEMPERATURE_CONFIG.STEP_K,
        value: (COLOR_TEMPERATURE_CONFIG.MIN_K + i * COLOR_TEMPERATURE_CONFIG.STEP_K).toString(),
    })
);
export const colorTemperatureArrayShorten = Array.from(
    { length: (COLOR_TEMPERATURE_CONFIG.CAP_K - COLOR_TEMPERATURE_CONFIG.MIN_K) / COLOR_TEMPERATURE_CONFIG.STEP_K + 1 },
    (_, i) => ({
        key: COLOR_TEMPERATURE_CONFIG.MIN_K + i * COLOR_TEMPERATURE_CONFIG.STEP_K,
        value: (COLOR_TEMPERATURE_CONFIG.MIN_K + i * COLOR_TEMPERATURE_CONFIG.STEP_K).toString(),
    })
);

export const whiteBalanceShiftBAArray = Array.from({ length: 9 + 9 + 1 }, (_, i) => ({
    key: -9 + i,
    value: (-9 + i).toString(),
}));

export const eosAutoPowerOffArray = Object.entries(eosAutoPowerOffMap).map(([key, value]) => ({
    key: parseInt(key),
    value,
}));

export interface NamesForSettings {
    propertyName: string;
    dataType: string;
    isEditMode: boolean;
    settingName: string;
    defaultSettingType: string;
    isChangeable: boolean;
    isHidden?: boolean;
    isAutoAdjust: boolean;
    currentFullOptions?: { list: { key: number; value: string }[]; min: number; max: number; step: number };
    defaultShortenOptions?: { list: { key: number; value: string }[]; min: number; max: number; step: number };
}

export const CameraPropertiesNullArray: SimpleCameraProperty[] = [
    {
        property: 'modeDial',
        value: 0,
    },
    {
        property: 'apertureValue',
        value: 4,
    },
    {
        property: 'focusMode',
        value: 0,
    },
    {
        property: 'shootingMode',
        value: 3,
    },
    {
        property: 'shutterSpeed',
        value: 12,
    },
    {
        property: 'whiteBalanceShiftBA',
        value: 9,
    },
    {
        property: 'isoSpeed',
        value: 5,
    },
    {
        property: 'colorTemperature',
        value: 21,
    },
    {
        property: 'meteringMode',
        value: 1,
    },
    {
        property: 'exposure',
        value: 0,
    },
    {
        property: 'whiteBalance',
        value: 0,
    },
    {
        property: 'stillPhotoImageQuality',
        value: [1, 14, 3],
    },
    {
        property: 'aspectRatio',
        value: 2,
    },
    {
        property: 'autoPowerOffTime',
        value: 0,
    },
];

export const DefaultCameraSettingsNames: NamesForSettings[] = [
    {
        propertyName: 'modeDial',
        isEditMode: false,
        settingName: 'Mode dial',
        defaultSettingType: 'dropdown',
        dataType: 'ENUM_VALUE',
        isChangeable: false,
        isHidden: true,
        isAutoAdjust: false,
    },
    {
        propertyName: 'apertureValue',
        isEditMode: false,
        settingName: 'Aperture',
        defaultSettingType: 'slider',
        currentFullOptions: {
            list: eosApertureValueArray,
            min: 0,
            max: eosApertureValueArray.length - 1,
            step: 1,
        },
        defaultShortenOptions: {
            list: eosApertureValueArrayShorten,
            min: 0,
            max: eosApertureValueArrayShorten.length - 1,
            step: 1,
        },
        dataType: 'ENUM_VALUE',
        isChangeable: true,
        isAutoAdjust: false,
    },
    {
        propertyName: 'focusMode',
        isEditMode: false,
        settingName: 'AF mode',
        defaultSettingType: 'dropdown',
        dataType: 'ENUM_VALUE',
        isChangeable: true,
        isAutoAdjust: false,
    },
    {
        propertyName: 'shootingMode',
        isEditMode: false,
        settingName: 'Shooting Mode',
        defaultSettingType: 'dropdown',
        dataType: 'ENUM_VALUE',
        isChangeable: true,
        isAutoAdjust: false,
    },
    {
        propertyName: 'shutterSpeed',
        isEditMode: false,
        settingName: 'Shutter speed',
        defaultSettingType: 'slider',
        currentFullOptions: { list: eosShutterSpeedArray, min: 0, max: eosShutterSpeedArray.length - 1, step: 1 },
        defaultShortenOptions: {
            list: eosShutterSpeedArrayShorten,
            min: 0,
            max: eosShutterSpeedArrayShorten.length - 1,
            step: 1,
        },
        dataType: 'ENUM_VALUE',
        isChangeable: true,
        isAutoAdjust: false,
    },
    {
        propertyName: 'whiteBalanceShiftBA',
        isEditMode: false,
        settingName: 'White balance shift BA',
        defaultSettingType: 'slider',
        currentFullOptions: {
            list: whiteBalanceShiftBAArray,
            min: 0,
            max: whiteBalanceShiftBAArray.length - 1,
            step: 1,
        },
        defaultShortenOptions: {
            list: whiteBalanceShiftBAArray,
            min: 0,
            max: whiteBalanceShiftBAArray.length - 1,
            step: 1,
        },
        dataType: 'VALUE',
        isChangeable: true,
        isAutoAdjust: false,
    },
    {
        propertyName: 'isoSpeed',
        isEditMode: false,
        settingName: 'ISO',
        defaultSettingType: 'slider',
        currentFullOptions: { list: eosIsoSpeedArray, min: 0, max: eosIsoSpeedArray.length - 1, step: 1 },
        defaultShortenOptions: {
            list: eosIsoSpeedArrayShorten,
            min: 0,
            max: eosIsoSpeedArrayShorten.length - 1,
            step: 1,
        },
        dataType: 'ENUM_VALUE',
        isChangeable: true,
        isAutoAdjust: false,
    },
    {
        propertyName: 'colorTemperature',
        isEditMode: false,
        settingName: 'Color temperature',
        defaultSettingType: 'slider',
        currentFullOptions: {
            list: colorTemperatureArray,
            min: 0,
            max: colorTemperatureArray.length - 1,
            step: 100,
        },
        defaultShortenOptions: {
            list: colorTemperatureArrayShorten,
            min: 0,
            max: colorTemperatureArrayShorten.length - 1,
            step: 100,
        },
        dataType: 'VALUE',
        isChangeable: true,
        isAutoAdjust: false,
    },
    {
        propertyName: 'driveMode',
        isEditMode: false,
        settingName: 'Drive mode',
        defaultSettingType: 'dropdown',
        dataType: 'ENUM_VALUE',
        isChangeable: true,
        isAutoAdjust: false,
    },
    {
        propertyName: 'meteringMode',
        isEditMode: false,
        settingName: 'Metering Mode',
        defaultSettingType: 'dropdown',
        dataType: 'ENUM_VALUE',
        isChangeable: true,
        isAutoAdjust: false,
    },
    {
        propertyName: 'exposure',
        isEditMode: false,
        settingName: 'Exposure',
        defaultSettingType: 'slider',
        currentFullOptions: {
            list: eosExposureCompensationArray,
            min: 0,
            max: eosExposureCompensationArray.length - 1,
            step: 1,
        },
        defaultShortenOptions: {
            list: eosExposureCompensationArrayShorten,
            min: 0,
            max: eosExposureCompensationArrayShorten.length - 1,
            step: 1,
        },
        dataType: 'ENUM_VALUE',
        isChangeable: true,
        isAutoAdjust: false,
    },
    {
        propertyName: 'whiteBalance',
        isEditMode: false,
        settingName: 'White balance',
        defaultSettingType: 'dropdown',
        dataType: 'ENUM_VALUE',
        isChangeable: true,
        isAutoAdjust: false,
    },
    {
        propertyName: 'stillPhotoImageQuality',
        isEditMode: false,
        settingName: 'Image quality',
        defaultSettingType: 'dropdown',
        dataType: 'ENUM_ARRAY',
        isChangeable: true,
        isAutoAdjust: false,
    },
    {
        propertyName: 'aspectRatio',
        isEditMode: false,
        settingName: 'Aspect ratio',
        defaultSettingType: 'dropdown',
        dataType: 'ENUM_VALUE',
        isChangeable: true,
        isAutoAdjust: false,
    },
    {
        propertyName: 'autoPowerOffTime',
        isEditMode: false,
        settingName: 'Auto power off',
        defaultSettingType: 'dropdown',
        dataType: 'VALUE',
        isChangeable: true,
        isAutoAdjust: false,
    },
];

export interface ForceDefaultSettings {
    isForced: boolean;
}
